import { Eye, EyeOff } from "lucide-react";
import { PublicHeaderWrapper } from "../../../_theme/layout/publicPagesComponent/HeaderWrapper";
import CustomFormInput from "../../modules/apps/components/CustomFormInput";
import { useCallback, useState, useEffect } from "react";
import { ILoginForm } from "../core/models";
import { LoginAPI } from "../core/request";
import { useSnackbar } from "../../../_theme/context/SnackbarContext";
import { getApiErrorMessage } from "../../../utils/utils";

import { Formik, Form, ErrorMessage } from "formik";
import * as Yup from "yup";
import { envConfig, EnvSwitch } from "../../../utils/config";
import { resolvePostLoginPath, useAuth } from "../../modules/auth";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { BackgroundVideo } from "./Component/backgroundVideo";
import { AppDispatch } from "../../../redux/store";
import { useDispatch } from "react-redux";
import { clearReduxAction } from "../../../redux/actions/auth.actions";
import passwordIcon from "../../../../public/media/svg/lock-icon.svg"
import usernameIcon from "../../../../public/media/svg/user-icon.svg"

/* =========================
   Validation Schema
========================= */
const LoginSchema = Yup.object().shape({
    uname: Yup.string().trim()
        .required("Username is required"),
    pwd: Yup.string()
        .required("Password is required")
});

/* =========================
   Main Component
========================= */
const Login: React.FC = () => {
    const { saveAuth } = useAuth();
    const { showSnackbar } = useSnackbar();
    const [loading, setLoading] = useState(false);
    const [showPassword, setShowPassword] = useState(false);
    const dispatch = useDispatch<AppDispatch>();
    const location = useLocation();
    const navigate = useNavigate();

    const handleSubmit = useCallback(async (values: ILoginForm) => {
        const payload: ILoginForm = {
            uname: values.uname,
            pwd: values.pwd,
        };
        setLoading(true);
        try {
            const response = await LoginAPI(payload);
            if (response.status === "ok") {
                const loginData = window.CookieUtils.decodefn(response.data, envConfig.APP_API_SECRET);
                if (!loginData) {
                    showSnackbar("Failed to decrypt login response", "danger");
                    return;
                }
                showSnackbar(response.message, "success");
                dispatch(clearReduxAction())
                saveAuth(JSON.parse(loginData));
                navigate(resolvePostLoginPath(location.search, location.state), { replace: true });

            } else {
                showSnackbar(response.message, "danger");
            }
        } catch (error) {
            showSnackbar(getApiErrorMessage(error, "Login failed"), "danger");
        } finally {
            setLoading(false);
        }
    }, [showSnackbar, saveAuth, dispatch, navigate, location.search, location.state]);

    useEffect(() => {
        document.body.classList.add("loginpage");
        return () => {
            document.body.classList.remove("loginpage");
        };
    }, []);

    return (
        <div className="position-relative vh-100 overflow-hidden">
            <PublicHeaderWrapper />
            <BackgroundVideo />

            <div className="home-section h-100">
                <div className="public-pages-container">
                    <div className="row px-2 px-xl-20 px-md-10 align-items-center">

                        {/* LEFT SIDE */}
                        <div className="col-12 col-md-5 pe-xl-20 px-sm-20 px-md-0  p-0 align-items-center d-flex flex-column">
                            <div className="login-card d-flex flex-column justify-content-center align-items-center w-100 gap-5">
                                <img src="/media/svg/logos/western-cape-government-logo-black.svg" alt="Logo" className="login-page-logo" />
                                {/* FORMIK FORM */}
                                <Formik
                                    initialValues={{
                                        uname: "",
                                        pwd: "",
                                    }}
                                    validationSchema={LoginSchema}
                                    onSubmit={handleSubmit}
                                >
                                    {({ setFieldValue, values }) => (
                                        <Form className="w-100 d-flex flex-column">
                                            <div className="mb-4 login-form-input ">
                                                <label className="form-label mb-1 login-field-label">Username</label>
                                                <div className="field-icon position-relative">
                                                    <CustomFormInput
                                                        placeholder="Enter your username"
                                                        type="text"
                                                        name="uname"
                                                        className="form-control"
                                                        value={values.uname || ""}
                                                        disabled={loading}
                                                        onChange={(e) => setFieldValue("uname", e.target.value)}
                                                    />
                                                    <img src={usernameIcon} alt="Username" className="svg-icon" />
                                                </div>
                                                <ErrorMessage name="uname" component="div" className="text-danger small mt-1" />
                                            </div>

                                            {/* Password */}
                                            <div className="mb-2 login-form-input has-password-toggle">
                                                <label className="form-label mb-1 login-field-label">Password</label>
                                                <div className="field-icon position-relative">
                                                    <CustomFormInput
                                                        placeholder="Enter your password"
                                                        name="pwd"
                                                        type={showPassword ? "text" : "password"}
                                                        value={values.pwd || ""}
                                                        disabled={loading}
                                                        className="form-control"
                                                        onChange={(e) => setFieldValue("pwd", e.target.value)}
                                                    />
                                                    <img src={passwordIcon} alt="Password" className="svg-icon" />
                                                    {values.pwd.length > 0 && (
                                                    <button
                                                        type="button"
                                                        className="password-toggle"
                                                        onClick={() => setShowPassword((prev) => !prev)}
                                                        aria-label={showPassword ? "Hide password" : "Show password"}
                                                        aria-pressed={showPassword}
                                                    >
                                                    {showPassword ? <Eye size={20} /> : <EyeOff size={20} />}
                                                    </button>
                                                    )}
                                                </div>
                                                <ErrorMessage name="pwd" component="div" className="text-danger small mt-1" />
                                            </div>
                                            <EnvSwitch newUI={<div className="text-end mt-3">
                                                <p><Link to="/auth/forgot-password" className="auth-link fw-bold">Forgot password?</Link></p>
                                            </div>} />
                                            {/* Submit Button */}
                                            <button
                                                type="submit"
                                                className="rounded-pill login-btn w-100"
                                                disabled={loading}
                                            >
                                                {loading ? "Logging in..." : "Login"}
                                            </button>
                                            <EnvSwitch
                                                newUI={
                                                    <div className="mt-8 text-center">
                                                        <p className="mb-0">New to the platform? <Link to="/auth/registration" className="auth-link fw-bold">Register</Link></p>
                                                    </div>
                                                } />
                                        </Form>
                                    )}
                                </Formik>
                            </div>
                        </div>

                        {/* RIGHT SIDE */}
                        <div className="col-12 col-md-7 ps-xl-20 px-md-10 ps-0 d-none d-md-block">
                            <p className="login-title  text-center">
                                One Platform. One Province. One Future.
                            </p>
                            <p className="text-center login-subtitle">
                                Step into the future with us and experience a vision of our 2050 infrastructure excellence.
                            </p>
                        </div>

                    </div>
                </div>
            </div>
        </div>
    );
};

export default Login;